import UploadFileIcon from "@mui/icons-material/UploadFile";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import MehrsanWorkQueueApi from "../../../../../infrastructure/api/mehrsanWorkQueue";

const pick = (obj, camel, pascal) => obj?.[camel] ?? obj?.[pascal] ?? null;

const normalizeImportResult = (raw) => {
  if (!raw) return null;
  const rows = pick(raw, "rows", "Rows") || [];
  return {
    confirm: pick(raw, "confirm", "Confirm"),
    success: pick(raw, "success", "Success"),
    message: pick(raw, "message", "Message") || "",
    totalRows: pick(raw, "totalRows", "TotalRows") ?? 0,
    matchedCount: pick(raw, "matchedCount", "MatchedCount") ?? 0,
    updatedCount: pick(raw, "updatedCount", "UpdatedCount") ?? 0,
    skippedCount: pick(raw, "skippedCount", "SkippedCount") ?? 0,
    errorCount: pick(raw, "errorCount", "ErrorCount") ?? 0,
    rows: (Array.isArray(rows) ? rows : []).map((r) => ({
      excelRowNumber: pick(r, "excelRowNumber", "ExcelRowNumber"),
      nationalCode: pick(r, "nationalCode", "NationalCode") || "",
      applicantName: pick(r, "applicantName", "ApplicantName") || "",
      mobile: pick(r, "mobile", "Mobile") || "",
      demandNumber: pick(r, "demandNumber", "DemandNumber") || "",
      currentStage: pick(r, "currentStage", "CurrentStage") || "",
      nextStage: pick(r, "nextStage", "NextStage") || "",
      requestInfoId: pick(r, "requestInfoId", "RequestInfoId"),
      requestTrackingCode: pick(r, "requestTrackingCode", "RequestTrackingCode"),
      mehrsanProcessId: pick(r, "mehrsanProcessId", "MehrsanProcessId"),
      outcome: pick(r, "outcome", "Outcome") || "",
      message: pick(r, "message", "Message") || "",
    })),
  };
};

const outcomeColor = (outcome) => {
  switch (outcome) {
    case "Updated":
    case "WouldUpdate":
    case "Matched":
      return "success";
    case "NotFound":
    case "Skipped":
    case "UnknownStage":
    case "Ambiguous":
      return "warning";
    case "Error":
      return "error";
    default:
      return "default";
  }
};

const outcomeLabel = (outcome) => {
  switch (outcome) {
    case "WouldUpdate":
      return "قابل اعمال";
    case "Updated":
      return "به‌روز شد";
    case "NotFound":
      return "یافت نشد";
    case "Skipped":
      return "رد شد";
    case "UnknownStage":
      return "مرحله ناشناخته";
    case "Ambiguous":
      return "مبهم";
    case "Error":
      return "خطا";
    default:
      return outcome || "—";
  }
};

/**
 * @param {{ open: boolean, source?: string, onClose: () => void, onCommitted?: () => void }} props
 */
const MehrsanPortalExcelImportDialog = ({
  open,
  source = "SolarentOperator",
  onClose,
  onCommitted,
}) => {
  const fileInputRef = useRef(null);
  const selectedFileRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const hasFile = Boolean(selectedFileRef.current) || Boolean(fileName);

  const summaryChips = useMemo(() => {
    if (!result) return [];
    return [
      { label: `کل: ${result.totalRows}`, color: "default" },
      { label: `قابل اعمال: ${result.matchedCount}`, color: "success" },
      { label: `به‌روز: ${result.updatedCount}`, color: "success" },
      { label: `ردشده: ${result.skippedCount}`, color: "warning" },
      { label: `خطا: ${result.errorCount}`, color: "error" },
    ];
  }, [result]);

  const resetState = () => {
    selectedFileRef.current = null;
    setFileName("");
    setResult(null);
    setLoading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    if (loading) return;
    resetState();
    onClose?.();
  };

  const handleFileChange = (e) => {
    const next = e.target.files?.[0] || null;
    selectedFileRef.current = next;
    setFileName(next?.name || "");
    setResult(null);
  };

  const runImport = async (confirm) => {
    const file = selectedFileRef.current || fileInputRef.current?.files?.[0] || null;
    if (!file) {
      toast.error("ابتدا فایل اکسل را انتخاب کنید.");
      return;
    }
    selectedFileRef.current = file;
    setFileName(file.name || "");
    setLoading(true);
    try {
      const res = await MehrsanWorkQueueApi.importPortalExcel(source, file, confirm);
      if (res?.isSuccess === false) {
        toast.error(res?.message || res?.Message || "خطا در ایمپورت اکسل مهرسان");
        return;
      }
      const data = normalizeImportResult(res?.data ?? res?.Data ?? res);
      setResult(data);
      if (confirm) {
        toast.success(data?.message || "همگام‌سازی اعمال شد");
        onCommitted?.();
      } else {
        toast.success(data?.message || "پیش‌نمایش آماده است");
      }
    } catch (e) {
      const apiMsg =
        e?.response?.data?.message ||
        e?.response?.data?.Message ||
        e?.message;
      toast.error(apiMsg || "خطا در ایمپورت اکسل مهرسان");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>آپلود خروجی اکسل مهرسان</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          فایل خروجی پورتال مهرسان با نام{" "}
          <strong>InstallationRequests_….xlsx</strong> را انتخاب کنید. ابتدا پیش‌نمایش
          بگیرید، سپس در صورت صحت، همگام‌سازی را اعمال کنید. تطبیق بر اساس کد ملی است.
        </Alert>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <Button
            component="label"
            variant="outlined"
            startIcon={<UploadFileIcon />}
            disabled={loading}
          >
            انتخاب فایل
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              hidden
              onChange={handleFileChange}
            />
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            {fileName || "فایلی انتخاب نشده است"}
          </Typography>
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={() => runImport(false)}
            disabled={!hasFile || loading}
          >
            پیش‌نمایش
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<SaveIcon />}
            onClick={() => runImport(true)}
            disabled={!hasFile || loading || !result}
          >
            اعمال همگام‌سازی
          </Button>
        </Stack>

        {result ? (
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {result.message}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              {summaryChips.map((c) => (
                <Chip key={c.label} size="small" label={c.label} color={c.color} variant="outlined" />
              ))}
            </Stack>

            <Box sx={{ maxHeight: 420, overflow: "auto", border: 1, borderColor: "divider", borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>ردیف اکسل</TableCell>
                    <TableCell>کد ملی</TableCell>
                    <TableCell>نام</TableCell>
                    <TableCell>مرحله جاری</TableCell>
                    <TableCell>کد پیگیری</TableCell>
                    <TableCell>نتیجه</TableCell>
                    <TableCell>پیام</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.rows.map((r) => (
                    <TableRow key={`${r.excelRowNumber}-${r.nationalCode}`}>
                      <TableCell>{r.excelRowNumber}</TableCell>
                      <TableCell>{r.nationalCode || "—"}</TableCell>
                      <TableCell>{r.applicantName || "—"}</TableCell>
                      <TableCell>{r.currentStage || "—"}</TableCell>
                      <TableCell>{r.requestTrackingCode || "—"}</TableCell>
                      <TableCell>
                        <Chip size="small" label={outcomeLabel(r.outcome)} color={outcomeColor(r.outcome)} />
                      </TableCell>
                      <TableCell sx={{ minWidth: 220 }}>{r.message || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          بستن
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MehrsanPortalExcelImportDialog;
